import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

declare global {
  interface Window {
    Razorpay: any
  }
}

export default function RazorpayDemo() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [selectedAmount, setSelectedAmount] = useState(4999)
  const [customAmount, setCustomAmount] = useState('')
  const [selectedCourse, setSelectedCourse] = useState('bridal-makeup')

  const courses = [
    { id: 'bridal-makeup', name: 'Professional Bridal Makeup Masterclass', price: 4999 },
    { id: 'hair-styling', name: 'Advanced Hair Styling Techniques', price: 3999 },
    { id: 'party-makeup', name: 'Party Makeup & Glamour', price: 2999 },
    { id: 'skincare', name: 'Skincare & Beauty Basics', price: 1999 }
  ]

  const quickAmounts = [999, 1999, 2999, 4999, 7999, 9999]

  const handlePayment = async () => {
    setLoading(true)

    try {
      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        alert('Razorpay SDK not loaded. Please refresh the page and try again.')
        setLoading(false)
        return
      }

      const amount = customAmount ? parseInt(customAmount) : selectedAmount
      const course = courses.find(c => c.id === selectedCourse)

      // Create order (in real app, this would be an API call)
      const order = {
        id: 'order_' + Math.random().toString(36).substr(2, 9),
        amount: amount * 100, // Razorpay amount is in paise
        currency: 'INR'
      }

      const options = {
        key: 'rzp_test_1234567890', // Demo key
        amount: order.amount,
        currency: order.currency,
        name: 'Sudha\'s Academy',
        description: course?.name || 'Course Payment',
        image: '/logo.png',
        order_id: order.id,
        handler: function (response: any) {
          // Payment successful
          console.log('Payment successful:', response)
          
          // Store payment details for success page
          const paymentData = {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            amount: amount,
            course: course
          }

          // Navigate to success page with payment details
          const params = new URLSearchParams({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            amount: amount.toString()
          })
          
          navigate(`/payment-success?${params.toString()}`)
        },
        prefill: {
          name: 'Student Name',
          email: 'student@example.com',
          contact: '9999999999'
        },
        notes: {
          course_id: selectedCourse,
          course_name: course?.name
        },
        theme: {
          color: '#D97706' // Gold color
        },
        modal: {
          ondismiss: function() {
            console.log('Payment cancelled by user')
            setLoading(false)
          }
        }
      }

      const paymentObject = new window.Razorpay(options)
      
      paymentObject.on('payment.failed', function (response: any) {
        console.error('Payment failed:', response.error)
        alert(`Payment failed: ${response.error.description}`)
        setLoading(false)
      })
      
      paymentObject.open()
      
    } catch (error) {
      console.error('Error initiating payment:', error)
      alert('Error initiating payment. Please try again.')
      setLoading(false)
    }
  }

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount)
    setCustomAmount('')
  }

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCustomAmount(value)
    if (value) {
      setSelectedAmount(parseInt(value) || 0)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-rose-100 dark:from-dark-900 dark:to-dark-800 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-charcoal-900 dark:text-white mb-2">
            Razorpay Payment Demo
          </h1>
          <p className="text-charcoal-600 dark:text-dark-300">
            Test the payment integration with Sudha's Academy courses
          </p>
        </div>

        {/* Payment Card */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-charcoal-200 dark:border-dark-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-600 to-rose-700 p-6 text-white">
            <h2 className="text-xl font-semibold mb-2">Course Payment</h2>
            <p className="text-rose-100">Complete your course enrollment</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Course Selection */}
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-dark-300 mb-3">
                Select Course
              </label>
              <div className="space-y-2">
                {courses.map((course) => (
                  <label key={course.id} className="flex items-center p-3 border border-charcoal-200 dark:border-dark-700 rounded-lg cursor-pointer hover:bg-charcoal-50 dark:hover:bg-dark-700">
                    <input
                      type="radio"
                      name="course"
                      value={course.id}
                      checked={selectedCourse === course.id}
                      onChange={(e) => {
                        setSelectedCourse(e.target.value)
                        const selectedCourseData = courses.find(c => c.id === e.target.value)
                        if (selectedCourseData) {
                          setSelectedAmount(selectedCourseData.price)
                          setCustomAmount('')
                        }
                      }}
                      className="mr-3 text-rose-600 focus:ring-rose-500"
                    />
                    <div className="flex-1 flex justify-between items-center">
                      <span className="text-charcoal-900 dark:text-white font-medium">
                        {course.name}
                      </span>
                      <span className="text-rose-600 dark:text-rose-400 font-bold">
                        ₹{course.price.toLocaleString()}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Amount Selection */}
            <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-dark-300 mb-3">
                Payment Amount
              </label>
              
              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {quickAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => handleAmountSelect(amount)}
                    className={`p-3 border rounded-lg font-medium transition-colors ${
                      selectedAmount === amount && !customAmount
                        ? 'border-rose-500 bg-rose-50 dark:bg-mauve-900/20 text-rose-700 dark:text-rose-300'
                        : 'border-charcoal-200 dark:border-dark-700 text-charcoal-700 dark:text-dark-300 hover:bg-charcoal-50 dark:hover:bg-dark-700'
                    }`}
                  >
                    ₹{amount.toLocaleString()}
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div>
                <label className="block text-xs text-charcoal-600 dark:text-dark-400 mb-2">
                  Or enter custom amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal-500 dark:text-dark-400">
                    ₹
                  </span>
                  <input
                    type="number"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    min="1"
                    className="w-full pl-8 pr-4 py-3 border border-charcoal-200 dark:border-dark-700 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 bg-white dark:bg-dark-800 text-charcoal-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="bg-charcoal-50 dark:bg-dark-700 rounded-lg p-4">
              <h3 className="font-medium text-charcoal-900 dark:text-white mb-2">Payment Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-charcoal-600 dark:text-dark-300">Course:</span>
                  <span className="text-charcoal-900 dark:text-white">
                    {courses.find(c => c.id === selectedCourse)?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-charcoal-600 dark:text-dark-300">Amount:</span>
                  <span className="text-charcoal-900 dark:text-white">
                    ₹{selectedAmount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-charcoal-600 dark:text-dark-300">Processing Fee:</span>
                  <span className="text-charcoal-900 dark:text-white">₹0</span>
                </div>
                <div className="border-t border-charcoal-200 dark:border-dark-600 pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span className="text-charcoal-900 dark:text-white">Total:</span>
                    <span className="text-rose-600 dark:text-rose-400">
                      ₹{selectedAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={loading || selectedAmount <= 0}
              className="w-full px-6 py-4 bg-rose-600 hover:bg-rose-700 disabled:bg-charcoal-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Pay ₹{selectedAmount.toLocaleString()}
                </>
              )}
            </button>

            {/* Test Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                📝 Test Payment Information
              </h4>
              <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <p><strong>Test Card Number:</strong> 4111 1111 1111 1111</p>
                <p><strong>CVV:</strong> Any 3 digits</p>
                <p><strong>Expiry:</strong> Any future date</p>
                <p><strong>UPI ID:</strong> success@razorpay (for UPI test)</p>
                <p><strong>Netbanking:</strong> Use any test bank</p>
              </div>
            </div>

            {/* Back Button */}
            <div className="text-center">
              <button
                onClick={() => navigate('/user/courses')}
                className="text-rose-600 dark:text-rose-400 hover:underline font-medium"
              >
                ← Back to Courses
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-dark-800 rounded-lg border border-charcoal-200 dark:border-dark-700 p-4 text-center">
            <div className="text-2xl mb-2">🔒</div>
            <h3 className="font-semibold text-charcoal-900 dark:text-white mb-1">Secure Payment</h3>
            <p className="text-sm text-charcoal-600 dark:text-dark-300">SSL encrypted and secure</p>
          </div>
          <div className="bg-white dark:bg-dark-800 rounded-lg border border-charcoal-200 dark:border-dark-700 p-4 text-center">
            <div className="text-2xl mb-2">💳</div>
            <h3 className="font-semibold text-charcoal-900 dark:text-white mb-1">Multiple Options</h3>
            <p className="text-sm text-charcoal-600 dark:text-dark-300">Cards, UPI, Net Banking</p>
          </div>
          <div className="bg-white dark:bg-dark-800 rounded-lg border border-charcoal-200 dark:border-dark-700 p-4 text-center">
            <div className="text-2xl mb-2">⚡</div>
            <h3 className="font-semibold text-charcoal-900 dark:text-white mb-1">Instant Access</h3>
            <p className="text-sm text-charcoal-600 dark:text-dark-300">Start learning immediately</p>
          </div>
        </div>
      </div>
    </div>
  )
}
